import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Icon, Span } from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { MultiStepFormLayout } from "components/multi-step-form-layout";
import { AuthAppContext } from "components/root/auth-app-provider";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { useTingEnrollment } from "pages/offer/ting/hooks";
import { TingContext, type TingContextState } from "pages/offer/ting/ting-flow";
import { formatTwoLineAddress } from "utils";

import styles from "./ting-offer.module.scss";

export const ContactInformationConfirmation = () => {
  const { policy } = useContext(AuthAppContext);
  const navigate = useNavigate();
  const { state, formNavigate } =
    useMultiStepFormState<TingContextState>(TingContext);

  const { enrollTing, loading, error } = useTingEnrollment(state);

  useEffect(() => {
    if (
      !(
        state?.phone &&
        state?.firstName &&
        state?.lastName &&
        state?.emailAddress
      )
    ) {
      navigate("../policy-confirmation");
    }
  }, [
    navigate,
    state?.emailAddress,
    state?.firstName,
    state?.lastName,
    state?.phone,
  ]);

  if (!policy.propertyAddress) {
    return null;
  }

  const onSubmit = async () => {
    const success = await enrollTing();
    if (success) {
      navigate("/my/property-services/ting-offer/registered");
    }
  };

  if (error) {
    throw new Error("error signing up for Ting offer");
  }

  const { lineOne, lineTwo } = formatTwoLineAddress(
    policy.propertyAddress,
    true
  );

  return (
    <MultiStepFormLayout
      step={4}
      totalSteps={4}
      heading="Please review and confirm your details"
      buttonProps={{
        primary: { text: "Submit", onPress: onSubmit, loading: loading },
        cancel: { path: "/my/property-services/ting-offer" },
        promptOnCancel: true,
        formNavigate,
      }}
    >
      <Stack>
        <div className={styles.iconItemSm}>
          <div className={styles.iconDS}>
            <Icon name="User" size="lg" />
          </div>
          <div>
            <Span bold>
              {state?.firstName} {state?.lastName}
            </Span>
            <div className={styles.subtext}>{state?.emailAddress}</div>
            <div className={styles.subtext}>{state?.phone}</div>
          </div>
        </div>
        <div className={styles.iconItemSm}>
          <div className={styles.iconDS}>
            <Icon name="Home" size="lg" />
          </div>
          <div>
            <Span bold>{lineOne}</Span>
            <br />
            <Span bold>{lineTwo}</Span>
            <div className={styles.subtext}>
              Policy number: {policy.policyId}
            </div>
          </div>
        </div>
      </Stack>
    </MultiStepFormLayout>
  );
};
